import React, { useState } from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  Badge,
  HStack,
  Icon,
  useColorModeValue,
  Flex,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  useToast,
} from '@chakra-ui/react';
import { FiBell, FiSearch, FiFilter, FiCheck, FiTrash2 } from 'react-icons/fi';
import {
  getNotifications,
  markNotificationAsRead,
  deleteNotification,
  markAllNotificationsAsRead,
} from '../../services/api';

const UserNotifications = () => {
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const toast = useToast();
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  // Dummy notifications data - replace with actual data from your backend
  const notifications = [
    {
      id: 1,
      title: 'Subscription Update',
      message: 'Your subscription will expire in 5 days',
      date: '2024-03-25',
      isRead: false,
      type: 'billing',
    },
    {
      id: 2,
      title: 'New Feature Available',
      message: 'Check out our new chatbot features',
      date: '2024-03-24',
      isRead: true,
      type: 'feature',
    },
    {
      id: 3,
      title: 'Security Alert',
      message: 'New login detected from a new device',
      date: '2024-03-23',
      isRead: false,
      type: 'security',
    },
  ];

  const handleMarkAsRead = (id) => {
    toast({
      title: 'Notification marked as read',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleDelete = (id) => {
    toast({
      title: 'Notification deleted',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'billing':
        return 'blue';
      case 'feature':
        return 'green';
      case 'security':
        return 'red';
      default:
        return 'gray';
    }
  };

  return (
    <Box p={5}>
      <VStack spacing={6} align="stretch">
        <Flex justify="space-between" align="center">
          <Heading size="lg">My Notifications</Heading>
          <Button
            leftIcon={<FiCheck />}
            colorScheme="purple"
            variant="outline"
            size="sm"
          >
            Mark All as Read
          </Button>
        </Flex>

        <Flex gap={4} wrap="wrap">
          <InputGroup maxW="300px">
            <InputLeftElement pointerEvents="none">
              <Icon as={FiSearch} color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search notifications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </InputGroup>

          <Select
            maxW="200px"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            icon={<FiFilter />}
          >
            <option value="all">All Notifications</option>
            <option value="unread">Unread</option>
            <option value="billing">Billing</option>
            <option value="feature">Features</option>
            <option value="security">Security</option>
          </Select>
        </Flex>

        <VStack spacing={4} align="stretch">
          {notifications.map((notification) => (
            <Box
              key={notification.id}
              p={4}
              bg={bgColor}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="lg"
              shadow="sm"
              _hover={{
                shadow: 'md',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s',
              }}
            >
              <Flex justify="space-between" align="start">
                <HStack spacing={3}>
                  <Icon
                    as={FiBell}
                    color={`${getNotificationColor(notification.type)}.500`}
                    boxSize={5}
                  />
                  <VStack align="start" spacing={1}>
                    <HStack>
                      <Heading size="sm">{notification.title}</Heading>
                      {!notification.isRead && (
                        <Badge colorScheme="blue" fontSize="xs">New</Badge>
                      )}
                    </HStack>
                    <Text color="gray.600">{notification.message}</Text>
                    <Text fontSize="sm" color="gray.500">
                      {notification.date}
                    </Text>
                  </VStack>
                </HStack>

                <Menu>
                  <MenuButton
                    as={Button}
                    variant="ghost"
                    size="sm"
                    px={2}
                  >
                    <Icon as={FiFilter} />
                  </MenuButton>
                  <MenuList>
                    <MenuItem
                      icon={<FiCheck />}
                      onClick={() => handleMarkAsRead(notification.id)}
                    >
                      Mark as Read
                    </MenuItem>
                    <MenuItem
                      icon={<FiTrash2 />}
                      onClick={() => handleDelete(notification.id)}
                      color="red.500"
                    >
                      Delete
                    </MenuItem>
                  </MenuList>
                </Menu>
              </Flex>
            </Box>
          ))}
        </VStack>
      </VStack>
    </Box>
  );
};

export default UserNotifications; 